import React from 'react';
import { Modal } from 'antd';
import PropTypes from 'prop-types';
import { I18n as I } from 'react-redux-i18n';
import {
  CheckboxList,
  CurrencyInput,
  Dropdown,
  LoaderOverlay,
  isFunc,
  isNullOrWhitespace,
  parseDecimal,
  PartTypes,
  ControlTypes,
  FieldInfo,
} from 'vdlib';

import LookupsHelper from 'lib/LookupsHelper';
import QuickBidPartPhotos from 'containers/bids/QuickBidPartPhotosContainer';

const defaultLookupStateValue = { value: null, dirty: false };
const bidTypesData = [
  {
    ID: PartTypes.New,
    Description: <i className="label label-success"> {I.t('PART_TYPE_NEW')}</i>,
  },
  {
    ID: PartTypes.Used,
    Description: <i className="label label-danger"> {I.t('PART_TYPE_USED')}</i>,
  },
  {
    ID: PartTypes.Imitation,
    Description: (
      <i className="label label-warning"> {I.t('PART_TYPE_IMITATION')} </i>
    ),
    className: 'col-3 col-offset-1',
  },
];

const partTypeData = [
  {
    color: 'green',
    partType: PartTypes.New,
    title: 'PRICE_NEW',
  },
  {
    color: 'red',
    partType: PartTypes.Used,
    title: 'PRICE_USED',
  },
  {
    color: 'yellow',
    partType: PartTypes.Imitation,
    title: 'PRICE_IMITATION',
  },
];

const defaultState = {
  comments: '',
  showNew: false,
  showUsed: false,
  showImitation: false,
  amountNew: 0,
  amountUsed: 0,
  amountImitation: 0,
  amountNewDirty: false,
  amountUsedDirty: false,
  amountImitationDirty: false,
};

const QuickBidModal = ({
  auctionId,
  bids,
  description,
  imitationCategories,
  loading,
  onHide,
  onSubmit,
  originPartType,
  partNumber,
  show,
  usedConditionTypes,
}) => {
  const [data, setData] = React.useState(defaultState);
  const [usedConditionTypeID, setUsedConditionTypeID] = React.useState(
    defaultLookupStateValue
  );
  const [imitationCategoryID, setImitationCategoryID] = React.useState(
    defaultLookupStateValue
  );

  const formContext = React.useRef({
    updateForm: (name, value) => {
      if (name === 'BidPartType')
        setData(d => ({ ...d, [`show${value.value.ID}`]: value.checked }));
      else if (name === 'ImitationCategoryID')
        setImitationCategoryID({ value, dirty: true });
      else if (name === 'UsedConditionTypeID')
        setUsedConditionTypeID({ value, dirty: true });
      else setData(d => ({ ...d, [name]: value, [`${name}Dirty`]: true }));
    },
  });

  // Initialize data based on existing bids
  React.useEffect(() => {
    if (Array.isArray(bids)) {
      const dataObj = {};

      const newBid = bids.find(
        b =>
          b.PartTypeID ===
          LookupsHelper.getIdByDescription('partTypes', PartTypes.New)
      );
      if (newBid) {
        dataObj.amountNew = newBid.Amount;
        dataObj.showNew =
          isNullOrWhitespace(originPartType) ||
          originPartType === PartTypes.New;
      }

      const usedBid = bids.find(
        b =>
          b.PartTypeID ===
          LookupsHelper.getIdByDescription('partTypes', PartTypes.Used)
      );
      if (usedBid) {
        dataObj.amountUsed = usedBid.Amount;
        dataObj.usedConditionTypeID = usedBid.UsedConditionTypeID;
        dataObj.showUsed =
          isNullOrWhitespace(originPartType) ||
          originPartType === PartTypes.Used;
      }

      const imitationBid = bids.find(
        b =>
          b.PartTypeID ===
          LookupsHelper.getIdByDescription('partTypes', PartTypes.Imitation)
      );
      if (imitationBid) {
        dataObj.amountImitation = imitationBid.Amount;
        dataObj.imitationCategoryID = imitationBid.ImitationCategoryID;
      }

      dataObj.showImitation =
        isNullOrWhitespace(originPartType) ||
        originPartType === PartTypes.Imitation;

      setData(dataObj);
    }
  }, [bids, originPartType]);

  // Reset state when hidden
  React.useEffect(() => {
    if (!show) {
      setData(defaultState);
      setImitationCategoryID(defaultLookupStateValue);
      setUsedConditionTypeID(defaultLookupStateValue);
    }
  }, [show]);

  const getAmountChangedHandler = partType => e => {
    const num = parseDecimal(e.target.value);
    if (!isNaN(num)) setData(d => ({ [`amount${partType}`]: num }));
  };

  const handleSubmit = React.useCallback(() => {
    const dataObj = {};
    for (let partType of Object.keys(PartTypes))
      if (data[`show${partType}`] === true && data[`amount${partType}`] === 0) {
        dataObj[`amount${partType}Dirty`] = true;
        dataObj['usedConditionTypeIDDirty'] = true;
        dataObj['imitationCategoryIDDirty'] = true;
      }

    if (Object.keys(dataObj).length > 0)
      return setData(d => ({ ...d, dataObj }));

    if (isFunc(onSubmit)) {
      onSubmit({
        bids: Object.keys(PartTypes)
          .filter(partType => data[`show${partType}`] === true)
          .map(partType => ({
            partType,
            amount: data[`amount${partType}`],
          })),
        comments: data.comments,
        usedConditionTypeID: usedConditionTypeID.value,
        imitationCategoryID: imitationCategoryID.value,
      });
    }
  }, [data, onSubmit, usedConditionTypeID, imitationCategoryID]);

  const bidTypes = React.useMemo(
    () => bidTypesData.map(bt => ({ ...bt, checked: data[`show${bt.ID}`] })),
    [data]
  );

  const inputs = React.useMemo(
    () =>
      partTypeData.map(({ partType, title, ...ptd }) => ({
        ...ptd,
        checked: data[`show${partType}`],
        onChange: getAmountChangedHandler(partType),
        partType,
        title: I.t(title),
        value: data[`amount${partType}`],
        // specifics
        usedCondition:
          partType === PartTypes.Used ? usedConditionTypeID.value : undefined,
        imiCategory:
          partType === PartTypes.Imitation
            ? imitationCategoryID.value
            : undefined,
      })),
    [data, usedConditionTypeID, imitationCategoryID]
  );

  return (
    <Modal
      className="animated-modal"
      closable
      maskClosable={false}
      visible={show}
      title={`${I.t('OFFER_FOR')} ${description || ''} - ${partNumber || ''}`}
      style={{ top: '12%' }}
      onCancel={onHide}
      footer={[
        <button
          type="button"
          className="btn btn-primary btn-sm float-right"
          onClick={handleSubmit}
        >
          {I.t('SAVE_INPUT')}
        </button>,
        <button
          type="button"
          className="btn btn-default btn-sm float-right mr-sm"
          onClick={onHide}
        >
          {I.t('CANCEL')}
        </button>,
      ]}
      width={data.showUsed ? '70%' : '35%'}
    >
      <div id="quick-bid-modal" className={data.showUsed ? 'expanded' : ''}>
        <LoaderOverlay loading={loading} />
        <div id="quick-bid-modal-body">
          <CheckboxList
            field={
              new FieldInfo(
                'BidPartType',
                '',
                null,
                bidTypes,
                formContext.current
              )
            }
            containerClassName="clearfix"
          />
          <hr className="clear" style={{ marginTop: '10px' }} />
          {inputs.map(inp => {
            const hasError =
              data[`amount${inp.partType}Dirty`] === true &&
              data[`amount${inp.partType}`] === 0;

            return (
              <div
                key={inp.color}
                className={`form-group bid-amount ${
                  inp.checked ? 'expanded' : ''
                } ${hasError ? 'has-error' : ''}`}
              >
                <div className="input-group right">
                  <span className={`input-group-addon bg-${inp.color}`}>
                    {inp.title}
                  </span>
                  <CurrencyInput
                    value={inp.value}
                    field={
                      new FieldInfo(
                        'amount' + inp.partType,
                        '',
                        inp.value,
                        null,
                        formContext.current,
                        ControlTypes.Currency
                      )
                    }
                    validationMessage={
                      hasError ? I.t('AMOUNT_GREATER_THAN_ZERO') : ''
                    }
                  />
                </div>
                {inp.partType === PartTypes.Imitation && (
                  <div className="input-group">
                    <span className={`input-group-addon bg-${inp.color}`}>
                      {I.t('CATEGORY')}
                    </span>
                    <Dropdown
                      inputClassName="mb0 dropdown-input"
                      value={inp.imiCategory}
                      field={
                        new FieldInfo(
                          'ImitationCategoryID',
                          'ΚατImitation',
                          inp.imiCategory,
                          imitationCategories,
                          formContext.current,
                          ControlTypes.Dropdown
                        )
                      }
                      validationMessage={hasError ? '' : ''}
                    />
                  </div>
                )}
                {inp.partType === PartTypes.Used && (
                  <>
                    {/* <div className="input-group">
                      <span className={`input-group-addon bg-${inp.color}`}>
                        {I.t('PHOTOS')}
                      </span>
                      <div style={{ flexGrow: 1 }}>
                        <Dropzone
                          accept="images"
                          onDrop={files => console.log("gotcha, doesn't work")}
                        />
                      </div>
                    </div> */}
                    <div className="input-group">
                      <span className={`input-group-addon bg-${inp.color}`}>
                        {I.t('STATUS')}
                      </span>
                      <Dropdown
                        inputClassName="mb0 dropdown-input"
                        value={inp.usedCondition}
                        field={
                          new FieldInfo(
                            'UsedConditionTypeID',
                            'ΚαταστΜεταχειρ',
                            inp.usedCondition,
                            usedConditionTypes,
                            formContext.current,
                            ControlTypes.Dropdown
                          )
                        }
                        validationMessage={''}
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <hr className="clear" />
          <div className="row form-group">
            <label className="control-label col-2">{I.t('COMMENTS')}</label>
            <div className="col-10">
              <textarea
                rows="4"
                className="form-control"
                value={data.comments}
                onChange={e =>
                  setData(d => ({ ...d, comments: e.target.value }))
                }
              />
            </div>
          </div>
        </div>
        <div id="quick-bid-modal-part-photos">
          <QuickBidPartPhotos auctionId={auctionId} />
        </div>
      </div>
    </Modal>
  );
};

QuickBidModal.propTypes = {
  bids: PropTypes.array,
  description: PropTypes.string,
  initialPartType: PropTypes.string,
  loading: PropTypes.bool,
  originPartType: PropTypes.oneOf(Object.values(PartTypes)),
  onHide: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  show: PropTypes.bool,
  usedConditionTypes: PropTypes.array,
  imitationCategories: PropTypes.array,
};

export default QuickBidModal;
